import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ChevronLeft,
  Save,
  Send,
  Sparkles,
  ImagePlus,
  Loader2,
  Wand2,
  Type,
  FileText,
  BookOpen,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { isNativePlatform, hasFullAccess } from '../lib/platform';

const CATEGORIES = [
  { id: 'investissement', label: "Stratégies d'investissement" },
  { id: 'fiscalite', label: 'Fiscalité immobilière' },
  { id: 'temoignages', label: 'Témoignages membres' },
  { id: 'actualites', label: 'Actualités du marché' },
  { id: 'guides', label: 'Guides & Tutoriels' },
];

const quillModules = {
  toolbar: [
    [{ 'header': [2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['blockquote'],
    ['link', 'image'],
    ['clean']
  ],
};

const BlogWritePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [category, setCategory] = useState('investissement');
  const [coverImage, setCoverImage] = useState('');
  const [tags, setTags] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [articleId, setArticleId] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPanel, setAiPanel] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');

  const tier = user?.subscription_tier || 'free';
  const isClub = hasFullAccess(tier);
  const isFounder = user?.email === 'rajasalame@gmail.com';

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#0D47A1] animate-spin" />
      </div>
    );
  }

  if (!isClub && !isFounder) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <Lock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Accès réservé</h2>
            <p className="text-slate-500 mb-4">Cette fonctionnalité sera bientôt disponible.</p>
            {!isNativePlatform() ? (
            <Link to="/pricing">
              <Button className="bg-gradient-to-r from-[#00B050] to-[#0D47A1] text-white">
                Rejoindre le Club
              </Button>
            </Link>
            ) : (
              <p className="text-sm text-slate-400">Bientôt disponible.</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Titre requis'); return; }
    setSaving(true);
    try {
      if (articleId) {
        await axios.put(`${API}/blog/articles/${articleId}`, {
          title, content, excerpt, category, cover_image: coverImage, tags: tags.split(',').map(t => t.trim()).filter(Boolean)
        }, { withCredentials: true });
        toast.success('Brouillon sauvegardé');
      } else {
        const res = await axios.post(`${API}/blog/articles`, {
          title, content, excerpt, category, cover_image: coverImage, tags: tags.split(',').map(t => t.trim()).filter(Boolean)
        }, { withCredentials: true });
        setArticleId(res.data.article_id);
        toast.success('Brouillon créé');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!articleId) await handleSave();
    if (!title.trim() || !content.trim()) { toast.error('Titre et contenu requis'); return; }
    setSubmitting(true);
    try {
      const id = articleId;
      if (!id) { toast.error('Sauvegardez d\'abord'); setSubmitting(false); return; }
      await axios.post(`${API}/blog/articles/${id}/submit`, {}, { withCredentials: true });
      toast.success('Article soumis pour validation !');
      navigate('/blog');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAI = async (action) => {
    setAiLoading(true);
    try {
      const payload = { action, prompt: aiPrompt || title || 'investissement immobilier', context: action === 'improve' ? content : '' };
      const res = await axios.post(`${API}/blog/ai/assist`, payload, { withCredentials: true });
      const result = res.data.result;
      if (action === 'generate' || action === 'improve') {
        setContent(result);
        toast.success('Contenu généré par l\'IA');
      } else if (action === 'title') {
        setAiPrompt(result);
        toast.success('Suggestions de titres générées');
      } else if (action === 'excerpt') {
        setExcerpt(result);
        toast.success('Résumé généré');
      }
    } catch (err) {
      toast.error('Erreur IA : ' + (err.response?.data?.detail || 'Réessayez'));
    } finally {
      setAiLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/blog/upload-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCoverImage(res.data.url);
      toast.success('Image ajoutée');
    } catch (err) {
      toast.error('Erreur d\'upload');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/blog" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm">
            <ChevronLeft className="w-4 h-4" />
            Retour au blog
          </Link>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAiPanel(!aiPanel)}
              className="text-[#0D47A1] border-[#0D47A1]/20"
              data-testid="ai-toggle"
            >
              <Sparkles className="w-4 h-4 mr-1" />
              Assistant IA
            </Button>
            <Button variant="outline" size="sm" onClick={handleSave} disabled={saving} data-testid="save-draft">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Brouillon
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-gradient-to-r from-[#00B050] to-[#0D47A1] text-white"
              data-testid="submit-article"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Soumettre
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-6 flex gap-6">
        {/* Main Editor */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <Input
            placeholder="Titre de l'article..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="text-2xl font-bold border-0 border-b rounded-none px-0 focus-visible:ring-0 mb-6 bg-transparent"
            data-testid="article-title"
          />

          {/* Cover image */}
          <div className="mb-6">
            {coverImage ? (
              <div className="relative rounded-xl overflow-hidden mb-2">
                <img src={coverImage.startsWith('/') ? `${API.replace('/api','')}${coverImage}` : coverImage} alt="Cover" className="w-full h-48 object-cover" />
                <button onClick={() => setCoverImage('')} className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">X</button>
              </div>
            ) : (
              <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-[#0D47A1]/50 transition-colors">
                <ImagePlus className="w-5 h-5 text-slate-400" />
                <span className="text-sm text-slate-500">Ajouter une image de couverture</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              </label>
            )}
          </div>

          {/* Category & Tags */}
          <div className="flex flex-wrap gap-3 mb-6">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="px-3 py-2 rounded-lg border text-sm bg-white"
              data-testid="article-category"
            >
              {CATEGORIES.map(c => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
            <Input
              placeholder="Tags (séparés par des virgules)"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className="flex-1 min-w-[200px]"
              data-testid="article-tags"
            />
          </div>

          {/* Excerpt */}
          <div className="mb-6">
            <label className="text-sm font-medium text-slate-600 mb-1 block">Résumé (SEO)</label>
            <textarea
              placeholder="Court résumé de l'article pour le référencement..."
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm resize-none h-20"
              data-testid="article-excerpt"
            />
          </div>

          {/* Rich Text Editor */}
          <div className="bg-white rounded-xl border overflow-hidden">
            <ReactQuill
              value={content}
              onChange={setContent}
              modules={quillModules}
              placeholder="Commencez à écrire votre article..."
              className="min-h-[400px]"
              data-testid="article-editor"
            />
          </div>
        </div>

        {/* AI Sidebar */}
        {aiPanel && (
          <div className="w-80 flex-shrink-0">
            <Card className="sticky top-20">
              <CardContent className="p-4">
                <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-[#0D47A1]" />
                  Assistant IA
                </h3>

                <div className="space-y-3">
                  <textarea
                    placeholder="Décrivez le sujet de votre article... Ex: 'Les avantages du déficit foncier pour réduire ses impôts'"
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm resize-none h-24"
                    data-testid="ai-prompt"
                  />

                  <Button
                    onClick={() => handleAI('generate')}
                    disabled={aiLoading}
                    className="w-full bg-gradient-to-r from-[#00B050] to-[#0D47A1] text-white"
                    data-testid="ai-generate"
                  >
                    {aiLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
                    Générer l'article
                  </Button>

                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAI('improve')}
                      disabled={aiLoading || !content}
                      data-testid="ai-improve"
                    >
                      <Wand2 className="w-3 h-3 mr-1" />
                      Améliorer
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAI('title')}
                      disabled={aiLoading}
                      data-testid="ai-title"
                    >
                      <Type className="w-3 h-3 mr-1" />
                      Titres
                    </Button>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAI('excerpt')}
                    disabled={aiLoading || !content}
                    className="w-full"
                    data-testid="ai-excerpt"
                  >
                    <FileText className="w-3 h-3 mr-1" />
                    Générer le résumé
                  </Button>
                </div>

                <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">
                    L'IA vous aide à rédiger. Vous gardez le contrôle total sur le contenu final.
                    L'article sera soumis à validation avant publication.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogWritePage;
